import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { APP_CONFIG, ImproverAngularEnvironment } from '@improver/app-config';
import { AuthorizedUser, LoggedUserService } from '@improver/infrastructure/auth-data-layer';
import { LanguageService } from '@improver/infrastructure/intl-data-layer';
import { filter, first } from 'rxjs/operators';
import { HelpZendeskWidgetConfiguration } from './help-zendesk-widget.interface';

@Injectable({ providedIn: 'root' })
export class HelpZendeskWidgetService {
  private window: any;
  private helpZendeskWidgetConfiguration: HelpZendeskWidgetConfiguration;

  constructor(
    @Inject(APP_CONFIG) private improverAngularEnvironment: ImproverAngularEnvironment,
    private router: Router,
    private loggedUserService: LoggedUserService,
    private languageService: LanguageService
  ) {
    this.window = this.getWindow();
    this.helpZendeskWidgetConfiguration = { zendeskHost: null };
  }

  setHelpCenterSuggestion(url: string, labels: string[] = []) {
    if (this.helpZendeskWidgetConfiguration.zendeskHost) {
      this.window.zE(() => {
        this.window.zE.setHelpCenterSuggestions({ url, labels });
      });
    }
  }

  init() {
    this.loggedUserService
      .getAuthorizedUserData()
      .pipe(
        filter((userData: AuthorizedUser) => !!userData && !!userData.iamUserId && !!userData.displayName),
        first()
      )
      .subscribe((userData: AuthorizedUser) => {
        this.initZendeskEmbedCode();
        this.updateZendeskData(userData);
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.setHelpCenterSuggestion(event.urlAfterRedirects, [event.urlAfterRedirects]);
    });
  }

  private updateZendeskData(userData: AuthorizedUser) {
    if (this.helpZendeskWidgetConfiguration.zendeskHost) {
      this.window.zE(() => {
        this.window.zE.identify({
          name: userData.displayName,
          email: userData.email,
        });
        this.setLanguage(userData.language);
      });
    }
  }

  private initZendeskEmbedCode() {
    const window: any = this.window;

    if (this.helpZendeskWidgetConfiguration.zendeskHost) {
      const zendeskHost = this.helpZendeskWidgetConfiguration.zendeskHost;

      this.configure();

      // Following is essentially a copy paste of JS portion of the Zendesk embed code.
      /* eslint-disable */
      window.zEmbed ||
        (function () {
          let n,
            o,
            d,
            i,
            s,
            a = [],
            r;

          r = document.createElement('iframe');
          window.zEmbed = function () {
            a.push(arguments);
          };
          window.zE = window.zE || window.zEmbed;
          r.src = 'javascript:false';
          r.title = '';
          r.style.cssText = 'display: none';
          d = document.getElementsByTagName('script');
          d = d[d.length - 1];
          d.parentNode.insertBefore(r, d);
          i = r.contentWindow;
          s = i.document;

          try {
            o = s;
          } catch (e) {
            n = document.domain;
            r.src = 'javascript:var d=document.open();d.domain="' + n + '";void(0);';
            o = s;
          }
          o.open()._l = function () {
            const e = this.createElement('script');

            n && (this.domain = n);
            e.id = 'js-iframe-async';
            e.src = 'https://assets.zendesk.com/embeddable_framework/main.js';
            this.t = +new Date();
            this.zendeskHost = zendeskHost;
            this.zEQueue = a;
            this.body.appendChild(e);
          };
          o.write('<body onload="document._l();">');
          o.close();
        })();
      /* tslint:enable */

      this.setLanguage(this.languageService.getCurrentLanguageCodeValue());
    }
  }

  private setLanguage(language: string) {
    if (this.helpZendeskWidgetConfiguration.zendeskHost) {
      this.window.zE(() => {
        this.window.zE.setLocale(language);
      });
    }
  }

  private configure() {
    this.window.zESettings = {
      webWidget: {
        color: {
          theme: '#033580',
        },
        zIndex: 1000001,
        position: {
          horizontal: 'right',
          vertical: 'bottom',
        },
        contactForm: {
          attachments: true,
          tags: [],
        },
      },
    };
  }

  private getWindow(): any {
    return window;
  }
}
